import { useState, useEffect } from 'react';
import { TrendingUp, TrendingDown, PieChart, BarChart3, RefreshCw, Coins } from 'lucide-react';
import { cryptoAPI } from '../../services/api';
import { toast } from 'sonner';
import { useUnifiedBalance } from '../../hooks/useUnifiedBalance';

const PortfolioTab = () => {
  const { 
    totalBalance, 
    availableBalance, 
    lockedBalance, 
    holdings, 
    prices,
    loading: balanceLoading, 
    refresh,
    lastUpdated
  } = useUnifiedBalance();
  
  const [cryptoList, setCryptoList] = useState([]);
  const [refreshing, setRefreshing] = useState(false);

  useEffect(() => {
    fetchCryptoData();
  }, []);

  const fetchCryptoData = async () => {
    try {
      const data = await cryptoAPI.getList(50).catch(() => []);
      setCryptoList(data);
    } catch (error) {
      console.error('Error fetching crypto data:', error);
    }
  };

  const refreshPortfolio = async () => {
    try {
      setRefreshing(true);
      refresh(); // Refresh balance data
      await fetchCryptoData(); // Refresh crypto list
      toast.success('Portfolio refreshed');
    } catch (error) {
      console.error('Error refreshing portfolio:', error);
      toast.error('Failed to refresh portfolio');
    } finally {
      setRefreshing(false);
    }
  };

  // Calculate portfolio stats
  const hasHoldings = holdings && holdings.length > 0;
  const totalValue = totalBalance || 0;
  
  // Calculate total profit (placeholder - would need purchase price tracking)
  const totalProfit = 0;
  const profitPercentage = 0;

  const isLoading = balanceLoading || refreshing;

  return (
    <div className="space-y-6 py-2">
      {/* Header with Refresh */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-2xl font-bold mb-2">Your <span className="gradient-text">Portfolio</span></h2>
          <p className="text-gray-400 text-sm sm:text-base">Track and manage your cryptocurrency investments</p>
          {lastUpdated && (
            <p className="text-gray-500 text-xs mt-1">
              Last updated: {new Date(lastUpdated).toLocaleTimeString()}
            </p>
          )}
        </div>
        <button
          onClick={refreshPortfolio}
          disabled={isLoading}
          className="p-3 bg-purple-600 hover:bg-purple-700 rounded-lg transition-colors disabled:opacity-50"
          title="Refresh Portfolio"
        >
          <RefreshCw className={`h-5 w-5 text-white ${isLoading ? 'animate-spin' : ''}`} />
        </button>
      </div>

      {/* Portfolio Overview */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 sm:gap-6">
        <div className="glow-card p-6 rounded-2xl">
          <div className="flex items-center justify-between mb-4">
            <div className="p-3 bg-gradient-to-br from-purple-600 to-blue-500 rounded-xl">
              <PieChart className="h-6 w-6 text-white" />
            </div>
          </div>
          <p className="text-gray-400 text-sm mb-1">Total Portfolio Value</p>
          <h3 className="text-3xl font-bold">
            {isLoading ? (
              <span className="animate-pulse">Loading...</span>
            ) : (
              `${totalValue.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} USDT`
            )}
          </h3>
        </div>

        <div className="glow-card p-6 rounded-2xl">
          <div className="flex items-center justify-between mb-4">
            <div className="p-3 bg-gradient-to-br from-green-600 to-emerald-500 rounded-xl">
              <TrendingUp className="h-6 w-6 text-white" />
            </div>
          </div>
          <p className="text-gray-400 text-sm mb-1">Available Balance</p>
          <h3 className="text-3xl font-bold text-green-400">
            {isLoading ? (
              <span className="animate-pulse">Loading...</span>
            ) : (
              `${availableBalance.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} USDT`
            )}
          </h3>
        </div>

        <div className="glow-card p-6 rounded-2xl">
          <div className="flex items-center justify-between mb-4">
            <div className="p-3 bg-gradient-to-br from-cyan-600 to-blue-500 rounded-xl">
              <Coins className="h-6 w-6 text-white" />
            </div>
          </div>
          <p className="text-gray-400 text-sm mb-1">Total Assets</p>
          <h3 className="text-3xl font-bold">{hasHoldings ? holdings.length : 0}</h3>
        </div>
      </div>

      {/* Holdings Table */}
      <div className="glow-card p-6 rounded-2xl">
        <h2 className="text-2xl font-bold mb-6">Asset Allocation</h2>
        
        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-4 border-purple-500 border-t-transparent"></div>
          </div>
        ) : !hasHoldings ? (
          <div className="text-center py-12">
            <Coins className="h-16 w-16 text-gray-600 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-gray-400 mb-2">No Holdings Yet</h3>
            <p className="text-gray-500">Your crypto holdings will appear here once you deposit or receive crypto.</p>
            <p className="text-gray-600 text-sm mt-2">Holdings are managed through the admin panel.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-purple-500/20">
                  <th className="text-left py-4 px-4 text-gray-400 font-semibold">Asset</th>
                  <th className="text-right py-4 px-4 text-gray-400 font-semibold">Amount</th>
                  <th className="text-right py-4 px-4 text-gray-400 font-semibold">Price</th>
                  <th className="text-right py-4 px-4 text-gray-400 font-semibold">Value (USDT)</th>
                  <th className="text-right py-4 px-4 text-gray-400 font-semibold">Available</th>
                  <th className="text-right py-4 px-4 text-gray-400 font-semibold">Allocation</th>
                </tr>
              </thead>
              <tbody>
                {holdings.map((holding, index) => {
                  const cryptoSymbol = holding.crypto_symbol || holding.crypto || 'UNKNOWN';
                  const cryptoId = holding.crypto_id || cryptoSymbol.toLowerCase();
                  
                  // Find crypto data for image and name
                  const cryptoData = cryptoList.find(c => 
                    c.symbol?.toUpperCase() === cryptoSymbol.toUpperCase() || 
                    c.id === cryptoId
                  );
                  
                  const holdingValue = holding.current_value || holding.usdt_value || 0;
                  const holdingAmount = holding.amount || holding.balance || 0;
                  const holdingAvailable = holding.available || holding.available_balance || holdingAmount;
                  const holdingPrice = holding.price || (prices && prices[cryptoSymbol]) || 0;
                  
                  const allocation = totalValue > 0 ? ((holdingValue / totalValue) * 100).toFixed(1) : 0;
                  
                  return (
                    <tr key={index} className="border-b border-gray-800/50 hover:bg-gray-900/30 transition-colors">
                      <td className="py-4 px-4">
                        <div className="flex items-center space-x-3">
                          {cryptoData && cryptoData.image ? (
                            <img src={cryptoData.image} alt={cryptoSymbol} className="w-8 h-8 rounded-full" />
                          ) : (
                            <div className="w-8 h-8 bg-gradient-to-br from-purple-600 to-blue-500 rounded-full flex items-center justify-center">
                              <span className="text-white font-bold text-xs">{cryptoSymbol.substring(0, 2)}</span>
                            </div>
                          )}
                          <div>
                            <p className="font-semibold">{cryptoSymbol.toUpperCase()}</p>
                            <p className="text-gray-400 text-sm">{cryptoData?.name || cryptoSymbol}</p>
                          </div>
                        </div>
                      </td>
                      <td className="text-right py-4 px-4">
                        <p className="font-semibold">{holdingAmount.toLocaleString('en-US', { maximumFractionDigits: 8 })}</p>
                      </td>
                      <td className="text-right py-4 px-4">
                        <p className="font-semibold text-gray-300">
                          ${holdingPrice.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </p>
                      </td>
                      <td className="text-right py-4 px-4">
                        <p className="font-semibold text-green-400">
                          {holdingValue.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} USDT
                        </p>
                      </td>
                      <td className="text-right py-4 px-4">
                        <p className="font-semibold">
                          {holdingAvailable.toLocaleString('en-US', { maximumFractionDigits: 8 })}
                        </p>
                      </td>
                      <td className="text-right py-4 px-4">
                        <div className="flex items-center justify-end space-x-2">
                          <div className="w-20 h-2 bg-gray-800 rounded-full overflow-hidden">
                            <div 
                              className="h-full bg-gradient-to-r from-purple-600 to-blue-500 rounded-full"
                              style={{ width: `${allocation}%` }}
                            ></div>
                          </div>
                          <span className="text-sm font-semibold">{allocation}%</span>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Locked Balance Info */}
      {lockedBalance > 0 && (
        <div className="glow-card p-6 rounded-2xl border border-yellow-500/30">
          <div className="flex items-center space-x-3 mb-4">
            <div className="p-3 bg-gradient-to-br from-yellow-600 to-orange-500 rounded-xl">
              <TrendingDown className="h-6 w-6 text-white" />
            </div>
            <div>
              <h3 className="text-xl font-bold">Locked Balance</h3>
              <p className="text-gray-400 text-sm">Funds currently in active trades or pending withdrawals</p>
            </div>
          </div>
          <p className="text-3xl font-bold text-yellow-400">
            {lockedBalance.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} USDT
          </p>
        </div>
      )}

      {/* Charts Placeholder */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="glow-card p-6 rounded-2xl">
          <h2 className="text-xl font-bold mb-4">Portfolio Performance</h2>
          <div className="h-64 flex items-center justify-center bg-gray-900/30 rounded-xl">
            <div className="text-center">
              <BarChart3 className="h-16 w-16 text-purple-500 mx-auto mb-4" />
              <p className="text-gray-400">Chart visualization coming soon</p>
            </div>
          </div>
        </div>

        <div className="glow-card p-6 rounded-2xl">
          <h2 className="text-xl font-bold mb-4">Asset Distribution</h2>
          <div className="h-64 flex items-center justify-center bg-gray-900/30 rounded-xl">
            {hasHoldings ? (
              <div className="w-full px-4">
                {holdings.slice(0, 5).map((holding, index) => {
                  const cryptoSymbol = holding.crypto_symbol || 'UNKNOWN';
                  const holdingValue = holding.current_value || 0;
                  const allocation = totalValue > 0 ? ((holdingValue / totalValue) * 100) : 0;
                  const colors = ['from-purple-500 to-blue-500', 'from-green-500 to-emerald-500', 'from-yellow-500 to-orange-500', 'from-cyan-500 to-blue-500', 'from-pink-500 to-rose-500'];
                  
                  return (
                    <div key={index} className="mb-3">
                      <div className="flex justify-between text-sm mb-1">
                        <span className="text-gray-300">{cryptoSymbol}</span>
                        <span className="text-gray-400">{allocation.toFixed(1)}%</span>
                      </div>
                      <div className="w-full h-3 bg-gray-800 rounded-full overflow-hidden">
                        <div 
                          className={`h-full bg-gradient-to-r ${colors[index % colors.length]} rounded-full transition-all duration-500`}
                          style={{ width: `${allocation}%` }}
                        ></div>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="text-center">
                <PieChart className="h-16 w-16 text-blue-500 mx-auto mb-4" />
                <p className="text-gray-400">Add holdings to see distribution</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PortfolioTab;
